import React, { Component } from 'react';
import { Ref } from 'semantic-ui-react';
import styled from 'styled-components';
import { IBuilding, IBuildingAreaCoords } from '@relab/alstom-ptw-shared';
import pointInPolygon from 'point-in-polygon';

import { MapAreaImage } from './MapAreaImage';

const Container = styled.div`
  position: relative;

  canvas {
    background-color: rgba(97, 218, 251, 0.05);
    position: absolute;
    top: 0;
  }
`;

interface MapAreaSelectorProps {
  selection?: IBuildingAreaCoords[];
  onSelected?: (coords: IBuildingAreaCoords[]) => void;
  onBuildingSelection?: (buildingId: string) => void;
  buildings?: IBuilding[];
}

interface MapAreaSelectorState {
  width?: number;
  height?: number;
}

export class MapAreaSelector extends Component<MapAreaSelectorProps, MapAreaSelectorState> {
  resizeObserver: any = null;
  image: HTMLImageElement = null;
  canvas: HTMLCanvasElement = null;
  ctx: CanvasRenderingContext2D = null;
  currentPathCoords: IBuildingAreaCoords[] = [];

  constructor(props: MapAreaSelectorProps) {
    super(props);

    this.state = {
      width: 300,
      height: 300,
    };
  }

  componentDidMount() {
    this.ctx = this.canvas.getContext('2d');
    this.ctx.strokeStyle = '#062173';
    this.ctx.lineWidth = 1;
    this.addMouseEvents();
    this.updateComponentSize();
    setTimeout(() => this.generateSelectionDrawing(this.props.selection), 100);

    // @ts-ignore
    this.resizeObserver = new ResizeObserver(() => {
      this.updateComponentSize();
    });
    this.resizeObserver.observe(this.image);
  }

  componentDidUpdate(prevProps: Readonly<MapAreaSelectorProps>) {
    if (this.props.selection !== prevProps.selection) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.generateSelectionDrawing(this.props.selection);
    }
  }

  componentWillUnmount() {
    this.removeMouseEvents();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  updateComponentSize() {
    if (this.image) {
      this.setState({
        width: this.image.offsetWidth,
        height: this.image.offsetHeight,
      });
      if (this.props.selection) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.generateSelectionDrawing(this.props.selection);
      }
    }
  }

  addMouseEvents() {
    this.canvas.addEventListener('mousedown', this.onMouseDown, false);
  }
  removeMouseEvents() {
    this.canvas.removeEventListener('mousedown', this.onMouseDown, false);
  }

  onMouseDown = (e: MouseEvent) => {
    if (this.props.onBuildingSelection) {
      const { x, y } = this.scaleCoordinates({ x: e.offsetX, y: e.offsetY });

      const clickedBuildingArea = this.props.buildings.find(
        (building) =>
          building?.buildingArea &&
          pointInPolygon(
            [x, y],
            building.buildingArea.map(({ x, y }) => [x, y])
          )
      );

      if (clickedBuildingArea) {
        this.props.onBuildingSelection(clickedBuildingArea._id);
      }
    } else {
      if (this.currentPathCoords.length === 0) {
        this.ctx.beginPath();
        this.ctx.moveTo(e.offsetX, e.offsetY);
      } else {
        this.ctx.lineTo(e.offsetX, e.offsetY);
      }
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.stroke();
      this.currentPathCoords.push(this.scaleCoordinates({ x: e.offsetX, y: e.offsetY }));

      if (
        this.currentPathCoords.length > 3 &&
        Math.abs(this.currentPathCoords[0].x - this.scaleCoordinates({ x: e.offsetX, y: e.offsetY }).x) < 15 &&
        Math.abs(this.currentPathCoords[0].y - this.scaleCoordinates({ x: e.offsetX, y: e.offsetY }).y) < 15
      ) {
        this.ctx.closePath();
        this.ctx.stroke();
        this.props.onSelected(this.currentPathCoords);
        this.ctx.beginPath();
        this.currentPathCoords = [];
      }
    }
  };

  generateSelectionDrawing = (selection: IBuildingAreaCoords[]) => {
    this.ctx.beginPath();
    this.ctx.strokeStyle = '#062173';
    this.ctx.lineWidth = 3;
    selection?.forEach((coord, index) => {
      const { x, y } = this.scaleCoordinates({ x: coord.x, y: coord.y }, true);
      if (index === 0) {
        this.ctx.moveTo(x, y);
      } else {
        this.ctx.lineTo(x, y);
      }
    });
    this.ctx.closePath();
    this.ctx.stroke();
  };

  scaleCoordinates = (coords: IBuildingAreaCoords, reverse?: boolean) => ({
    x: reverse
      ? (this.state.width * coords.x) / this.image.naturalWidth
      : (this.image.naturalWidth * coords.x) / this.state.width,
    y: reverse
      ? (this.state.height * coords.y) / this.image.naturalHeight
      : (this.image.naturalHeight * coords.y) / this.state.height,
  });

  render() {
    return (
      <Container>
        <Ref
          innerRef={(instance) => {
            this.image = instance;
          }}
        >
          <MapAreaImage />
        </Ref>
        <canvas
          width={this.state.width}
          height={this.state.height}
          ref={(c) => {
            this.canvas = c;
          }}
        />
      </Container>
    );
  }
}
